import { Component, inject, OnInit, ViewEncapsulation } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { ActivatedRoute, Router } from '@angular/router';
import { AbstractControl, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { TokenService } from '@services/token-service';
import { I18nPlaceholderDirective } from '../../directives/i18n.directives';
import { IconDirective } from '../../directives/icon.directive';
import { CopyrightComponent } from '../copyright/copyright.component';
import { take } from 'rxjs/operators';

@Component({
  selector: 'lv-reset-password',
  templateUrl: 'reset-password.component.html',
  styleUrls: ['reset-password.component.scss'],
  encapsulation: ViewEncapsulation.None,
  imports: [ReactiveFormsModule, I18nPlaceholderDirective, IconDirective, TranslateModule, CopyrightComponent]
})
export class ResetPasswordComponent implements OnInit {
  private tokenService = inject(TokenService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private translate = inject(TranslateService);

  loading = false;
  errorMessage = '';

  form = new FormGroup(
    {
      password: new FormControl('', [Validators.required, Validators.minLength(6)]),
      passwordConfirmation: new FormControl('', [Validators.required, Validators.minLength(6)]),
      resetPasswordToken: new FormControl('', Validators.required)
    },
    { validators: this.passwordMatchValidator }
  );

  ngOnInit() {
    this.route.queryParams.pipe(take(1)).subscribe((params) => {
      const token = params['reset_password_token'];
      if (!token) {
        this.router.navigate(['/auth/password/token-expired']);
      } else {
        this.form.patchValue({ resetPasswordToken: token });
      }
    });
  }

  resetPassword() {
    if (this.form.invalid) {
      if (this.form.errors?.['passwordMismatch']) {
        this.errorMessage = this.translate.instant('reset-password.passwords-do-not-match');
      }
      return;
    }

    const { password, passwordConfirmation, resetPasswordToken } = this.form.value;

    this.loading = true;
    this.errorMessage = '';

    this.tokenService
      .updatePassword({
        password,
        passwordConfirmation,
        resetPasswordToken
      })
      .subscribe({
        next: () => {
          this.router.navigate(['/auth/password/success']);
        },
        error: (err) => this.handleResetPasswordError(err)
      });
  }

  private handleResetPasswordError(err: HttpErrorResponse) {
    this.loading = false;
    if (err.status === 401 || err.status === 404) {
      this.router.navigate(['/auth/password/token-expired']);
    } else {
      const backendMessage = err.error?.errors?.full_messages?.join(', ');
      this.errorMessage = backendMessage || this.translate.instant('reset-password.error-generic');
    }
  }

  private passwordMatchValidator(control: AbstractControl) {
    const password = control.get('password')?.value;
    const passwordConfirmation = control.get('passwordConfirmation')?.value;

    if (!password || !passwordConfirmation) {
      return null;
    }

    return password === passwordConfirmation ? null : { passwordMismatch: true };
  }
}
